import { useNavigate, Link } from 'react-router-dom';
import { MarketingLayout } from '@/components/MarketingLayout';
import { Seo } from '@/components/Seo';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowRight, Check, Minus, X, Zap, Clock, Bot, UserCog, Sparkles } from 'lucide-react';

const AS_OF = 'July 2026';

// Speed-of-workflow comparison — the headline differentiator.
const SPEED = [
  { step: 'COI review', nv: 'Hours–Days (human queue)', vs: 'Seconds (automated)' },
  { step: 'License verification', nv: 'Hours–Days', vs: 'Seconds' },
  { step: 'Vendor onboarding', nv: 'After human approval', vs: 'Instant' },
  { step: 'Document renewal', nv: 'Manual + human review', vs: 'Fully automated' },
  { step: 'Exception handling', nv: 'Human interpretation', vs: 'Deterministic logic' },
];

// vs/nv status: 'yes' | 'partial' | 'no'. NetVendor cells are sourced from netvendor.com
// and public press (see note below the table) and should be re-verified over time.
const CRITERIA = [
  { feature: 'Automated compliance (no human queue)', vs: 'Yes — switch on automation to clear vendors in seconds', vsStatus: 'yes', nv: 'Enforcement is human-review powered', nvStatus: 'no' },
  { feature: 'You choose when automation runs', vs: 'Yes — automation ships off; enable it when ready', vsStatus: 'yes', nv: 'Human review is the default path, not a toggle', nvStatus: 'partial' },
  { feature: 'AI document extraction (OCR + field understanding)', vs: 'Yes — OCR + LLM field extraction', vsStatus: 'yes', nv: 'Yes — AI added across compliance (2026)', nvStatus: 'yes' },
  { feature: 'Passwordless vendor portal (magic link)', vs: 'Yes — no account or login for vendors', vsStatus: 'yes', nv: 'Vendor onboarding portal (account-based)', nvStatus: 'partial' },
  { feature: 'Automated COI renewal monitoring & chasing', vs: 'Yes — escalating auto-chase engine', vsStatus: 'yes', nv: 'Yes — automated renewal monitoring & alerts', nvStatus: 'yes' },
  { feature: 'Background checks, OFAC & TIN screening', vs: 'Not offered (focus on insurance, licenses, W-9)', vsStatus: 'no', nv: 'Yes — background checks, OFAC, TIN matching', nvStatus: 'yes' },
  { feature: 'Native PMS integrations (Yardi, Entrata, RealPage, AppFolio, MRI)', vs: 'Not built-in yet', vsStatus: 'no', nv: 'Yes — native, multi-PMS sync', nvStatus: 'yes' },
  { feature: 'Per-property & per-vendor requirement rules', vs: 'Yes — 3-layer override system', vsStatus: 'yes', nv: 'Requirement management (per-property depth not specified)', nvStatus: 'partial' },
  { feature: 'Portfolio-wide compliance dashboard', vs: 'Yes — real-time scoring', vsStatus: 'yes', nv: 'Yes — real-time dashboards & reporting', nvStatus: 'yes' },
  { feature: 'Transparent, published pricing', vs: 'Yes — from $249/mo + $4.50/vendor', vsStatus: 'yes', nv: 'Not published — quote-based', nvStatus: 'no' },
  { feature: 'Free self-serve trial (no sales call)', vs: 'Yes — 30 days, no credit card', vsStatus: 'yes', nv: 'Demo / contact sales', nvStatus: 'no' },
];

const StatusIcon = ({ status }) => {
  if (status === 'yes') return <Check className="h-4 w-4 text-primary mt-0.5 shrink-0" />;
  if (status === 'no') return <X className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />;
  return <Minus className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />;
};

const FAQS = [
  {
    q: 'What is the real difference between VendorSuite and NetVendor?',
    a: 'The core difference is architectural. VendorSuite is automation-ready: an AI + OCR extraction pipeline and a deterministic rules engine can review, score, and enforce compliance in seconds. Automation ships off by default so you start fully in control—and the moment you switch it on, vendors onboard instantly and documents are scored in seconds. NetVendor is compliance-led with a human review team, so enforcement—while robust—runs at human speed. VendorSuite also differentiates on transparent published pricing and a free self-serve trial. NetVendor offers broader lifecycle scope (background checks, OFAC/TIN screening, native PMS integrations), typically sold via custom quote. As of July 2026; verify current details with NetVendor.',
  },
  {
    q: 'Is the automation optional, or am I forced into it?',
    a: 'It is entirely under your control. VendorSuite ships with automation turned off, so you begin by reviewing everything yourself and stay fully in control. When you are ready, you switch automation on—and that is when vendors onboard instantly and documents are scored in seconds. We strongly recommend enabling it, because that is where the real ROI shows up: your team stops living in a review queue.',
  },
  {
    q: 'How should I evaluate vendor compliance software?',
    a: 'Focus on five things: (1) how documents are captured and read (manual vs. AI), (2) whether enforcement runs automatically or waits on a human queue, (3) how easy it is for vendors to submit documents, (4) whether requirements can vary by property and vendor type, and (5) how renewals/expirations are handled automatically.',
  },
  {
    q: 'Does VendorSuite offer a free trial?',
    a: 'Yes. VendorSuite offers a free 30-day trial with no credit card required, so you can evaluate the full automated workflow before committing.',
  },
];

export default function ComparePage() {
  const navigate = useNavigate();

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };

  return (
    <MarketingLayout>
      <Seo
        title="VendorSuite vs NetVendor: Automated vs Human-Reviewed Compliance"
        description="A side-by-side comparison of vendor compliance software for property managers. See why automation-led compliance clears vendors in seconds vs. a human review queue—plus a full speed and architecture breakdown."
        path="/compare/vendorsuite-vs-netvendor"
        type="article"
        jsonLd={jsonLd}
      />
      <div data-testid="compare-page" className="max-w-4xl mx-auto px-6 py-14">
        <Badge variant="secondary" className="mb-4">Comparison</Badge>
        <h1 className="text-4xl font-bold mb-4 leading-tight" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
          VendorSuite vs NetVendor: automated compliance vs. a human review queue
        </h1>
        <p className="text-lg text-muted-foreground mb-8">
          Both platforms keep vendors insured and compliant. The difference is <em>how</em>: NetVendor enforces
          compliance with a human review team, while VendorSuite gives you an automation engine you can{' '}
          <strong className="text-foreground">switch on</strong>—and the moment you do, documents clear in seconds and
          vendors onboard instantly instead of waiting in a queue.
        </p>

        {/* No-brainer callout: automation ships off, turn it on for ROI */}
        <div
          data-testid="optional-automation-callout"
          className="rounded-2xl border border-primary/30 bg-primary/5 p-6 mb-12"
        >
          <span className="inline-flex items-center gap-2 text-primary font-semibold mb-2">
            <Zap className="h-5 w-5" /> Automation you switch on — and instantly feel.
          </span>
          <p className="text-sm text-foreground/90 leading-relaxed mb-3">
            Here&apos;s the part most teams miss when comparing tools: VendorSuite ships with automation turned{' '}
            <strong>off</strong>, so you start fully in control and review everything yourself. The moment you&apos;re
            comfortable, you flip automation <strong>on</strong>—and every COI, license, and W-9 is read, scored, and
            cleared in seconds instead of days.
          </p>
          <p className="text-sm text-foreground/90 leading-relaxed">
            That&apos;s where the ROI shows up. Once automation is enabled, vendors onboard instantly, renewals handle
            themselves, and your team stops living in a review queue. We strongly recommend turning it on—it&apos;s the
            difference between software that saves you a little time and software that saves you a whole headcount.
          </p>
        </div>

        {/* Speed comparison */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-4 flex items-center gap-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            <Clock className="h-6 w-6 text-primary" /> Speed comparison
          </h2>
          <div className="overflow-x-auto rounded-2xl border">
            <table className="w-full text-sm" data-testid="speed-table">
              <thead>
                <tr className="border-b bg-muted/40 text-left">
                  <th className="p-4 font-semibold">Workflow step</th>
                  <th className="p-4 font-semibold">NetVendor</th>
                  <th className="p-4 font-semibold text-primary">VendorSuite</th>
                </tr>
              </thead>
              <tbody>
                {SPEED.map((row, i) => (
                  <tr key={i} className="border-b last:border-0">
                    <td className="p-4 align-top font-medium">{row.step}</td>
                    <td className="p-4 align-top text-muted-foreground">{row.nv}</td>
                    <td className="p-4 align-top">
                      <span className="inline-flex items-center gap-1.5 font-medium text-foreground">
                        <Zap className="h-4 w-4 text-primary shrink-0" /> {row.vs}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-muted-foreground mt-3">
            VendorSuite times reflect the workflow with automation enabled. Automation ships off by default so you stay
            in control—switch it on to unlock these speeds.
          </p>
        </section>

        {/* Full feature table */}
        <section className="mb-3">
          <h2 className="text-2xl font-semibold mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            Full feature comparison
          </h2>
          <div className="overflow-x-auto rounded-2xl border">
            <table className="w-full text-sm" data-testid="compare-table">
              <thead>
                <tr className="border-b bg-muted/40 text-left">
                  <th className="p-4 font-semibold">What to look for</th>
                  <th className="p-4 font-semibold text-primary">VendorSuite</th>
                  <th className="p-4 font-semibold">NetVendor</th>
                </tr>
              </thead>
              <tbody>
                {CRITERIA.map((row, i) => (
                  <tr key={i} className="border-b last:border-0">
                    <td className="p-4 align-top">{row.feature}</td>
                    <td className="p-4 align-top">
                      <span className="inline-flex items-start gap-1.5 text-foreground">
                        <StatusIcon status={row.vsStatus} /> {row.vs}
                      </span>
                    </td>
                    <td className="p-4 align-top text-muted-foreground">
                      <span className="inline-flex items-start gap-1.5">
                        <StatusIcon status={row.nvStatus} /> {row.nv}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <p className="text-xs text-muted-foreground mt-3" data-testid="compare-asof-note">
          VendorSuite details reflect our current product. NetVendor details are based on publicly available information
          from netvendor.com and press releases, accurate as of {AS_OF}. Vendor features and pricing change—verify current
          details directly with NetVendor before making a decision.
        </p>

        {/* Architectural difference */}
        <section className="mt-12">
          <h2 className="text-2xl font-semibold mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            The architectural difference
          </h2>
          <div className="grid gap-5 sm:grid-cols-2">
            <div className="rounded-2xl border p-6" data-testid="arch-netvendor">
              <span className="inline-flex items-center gap-2 font-semibold mb-3">
                <UserCog className="h-5 w-5 text-muted-foreground" /> NetVendor
              </span>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="flex items-start gap-2"><Minus className="h-4 w-4 mt-0.5 shrink-0" /> Compliance-led model</li>
                <li className="flex items-start gap-2"><Minus className="h-4 w-4 mt-0.5 shrink-0" /> Human review team</li>
                <li className="flex items-start gap-2"><Minus className="h-4 w-4 mt-0.5 shrink-0" /> Enforcement gate</li>
                <li className="flex items-start gap-2"><Minus className="h-4 w-4 mt-0.5 shrink-0" /> Vendor operations + credentialing</li>
              </ul>
            </div>
            <div className="rounded-2xl border border-primary/30 bg-primary/5 p-6" data-testid="arch-vendorsuite">
              <span className="inline-flex items-center gap-2 font-semibold mb-3 text-primary">
                <Bot className="h-5 w-5" /> VendorSuite
              </span>
              <ul className="space-y-2 text-sm text-foreground/90">
                <li className="flex items-start gap-2"><Check className="h-4 w-4 mt-0.5 shrink-0 text-primary" /> Automation-led model</li>
                <li className="flex items-start gap-2"><Check className="h-4 w-4 mt-0.5 shrink-0 text-primary" /> AI/OCR + rules engine</li>
                <li className="flex items-start gap-2"><Check className="h-4 w-4 mt-0.5 shrink-0 text-primary" /> Deterministic compliance</li>
                <li className="flex items-start gap-2"><Check className="h-4 w-4 mt-0.5 shrink-0 text-primary" /> Built for speed, scale &amp; zero human bottlenecks</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Why this matters */}
        <section className="mt-12">
          <h2 className="text-2xl font-semibold mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            Why this matters for PM companies
          </h2>
          <div className="grid gap-5 sm:grid-cols-2">
            <div className="rounded-2xl border p-6">
              <h3 className="font-semibold mb-3">VendorSuite advantages</h3>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="flex items-start gap-2"><Check className="h-4 w-4 mt-0.5 shrink-0 text-primary" /> Faster onboarding — vendors don&apos;t wait days</li>
                <li className="flex items-start gap-2"><Check className="h-4 w-4 mt-0.5 shrink-0 text-primary" /> Lower PM workload — no back-and-forth with a review team</li>
                <li className="flex items-start gap-2"><Check className="h-4 w-4 mt-0.5 shrink-0 text-primary" /> Lower vendor friction — instant feedback</li>
                <li className="flex items-start gap-2"><Check className="h-4 w-4 mt-0.5 shrink-0 text-primary" /> Higher accuracy — deterministic rules reduce human error</li>
                <li className="flex items-start gap-2"><Check className="h-4 w-4 mt-0.5 shrink-0 text-primary" /> Scalable — automation handles thousands of vendors without staffing</li>
              </ul>
            </div>
            <div className="rounded-2xl border p-6">
              <h3 className="font-semibold mb-3">NetVendor advantages</h3>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="flex items-start gap-2"><Minus className="h-4 w-4 mt-0.5 shrink-0" /> Human review can catch nuanced edge cases</li>
                <li className="flex items-start gap-2"><Minus className="h-4 w-4 mt-0.5 shrink-0" /> PM companies can outsource compliance labor</li>
              </ul>
              <p className="text-xs text-muted-foreground mt-4">
                Note: VendorSuite lets you keep humans in the loop wherever you choose—so you can layer that same
                edge-case oversight on top of automation instead of running a human queue on every vendor.
              </p>
            </div>
          </div>
        </section>

        {/* Non-obvious insight */}
        <section className="mt-12">
          <div className="rounded-2xl border bg-muted/30 p-8" data-testid="insight-callout">
            <span className="inline-flex items-center gap-2 text-primary font-semibold mb-3">
              <Sparkles className="h-5 w-5" /> The non-obvious insight
            </span>
            <p className="text-foreground/90 leading-relaxed mb-4">
              NetVendor&apos;s model <em>sounds</em> automated because compliance is enforced—but the enforcement is
              human-powered. VendorSuite&apos;s model is machine-powered, meaning both enforcement and validation are
              automated. It&apos;s the same distinction as:
            </p>
            <div className="grid gap-4 sm:grid-cols-2 mb-4">
              <div className="rounded-xl border bg-background p-4">
                <p className="font-semibold text-sm mb-1">TurboTax Live</p>
                <p className="text-xs text-muted-foreground">Rules enforced, but a human reviews your return.</p>
              </div>
              <div className="rounded-xl border border-primary/30 bg-primary/5 p-4">
                <p className="font-semibold text-sm mb-1 text-primary">Stripe Radar</p>
                <p className="text-xs text-muted-foreground">An automated rules engine enforces in real time.</p>
              </div>
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Both enforce rules. Only one is <strong className="text-foreground">truly automated</strong>—and that&apos;s
              the difference between clearing a vendor in seconds and waiting on a review queue.
            </p>
          </div>
        </section>

        {/* Where each is strong */}
        <section className="mt-12">
          <h2 className="text-2xl font-semibold mb-3" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            Where each platform is strong
          </h2>
          <p className="text-muted-foreground leading-relaxed mb-3">
            <strong className="text-foreground">NetVendor</strong> is a mature, award-winning platform with the widest
            scope: beyond insurance and licensing it adds background checks, OFAC and TIN screening, native two-way
            integrations with major property management systems (Yardi, Entrata, RealPage, AppFolio, MRI), and—more
            recently—vendor sourcing and bidding across the full vendor lifecycle. If deep PMS enforcement and screening
            breadth are your top priorities, it is a strong fit; pricing is quote-based.
          </p>
          <p className="text-muted-foreground leading-relaxed">
            <strong className="text-foreground">VendorSuite</strong> optimizes for speed and a low-friction experience:
            an automation engine you switch on when ready, transparent published pricing, a free 30-day self-serve trial
            with no sales call, a passwordless magic-link vendor portal, and per-property/per-vendor requirement rules.
            If you want to be live this week without procurement overhead—and clear vendors in seconds instead of
            days—that is the difference.
          </p>
        </section>

        {/* FAQ */}
        <section className="mt-12">
          <h2 className="text-2xl font-semibold mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            Common questions
          </h2>
          <div className="space-y-5">
            {FAQS.map((f, i) => (
              <div key={i}>
                <h3 className="font-semibold mb-1">{f.q}</h3>
                <p className="text-muted-foreground text-sm leading-relaxed">{f.a}</p>
              </div>
            ))}
          </div>
        </section>

        <div className="mt-14 rounded-2xl border bg-muted/30 p-8 text-center">
          <h2 className="text-xl font-bold mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            See automated compliance for yourself
          </h2>
          <p className="text-muted-foreground text-sm mb-5">
            Free 30-day trial, no credit card, no sales call required.
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            <Button onClick={() => navigate('/signup')} data-testid="compare-cta-button">
              Start Free 30-Day Trial <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
            <Button variant="outline" asChild>
              <Link to="/guides">Read the guides</Link>
            </Button>
          </div>
        </div>
      </div>
    </MarketingLayout>
  );
}
